import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { loginRequest, silentRequest } from './authConfig';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  getAccessToken: (scopes: string[]) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = accounts.length > 0;
  const account = accounts[0] || null;

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if we have an active account
        if (accounts.length === 0) {
          // Try silent authentication first
          try {
            const silentResult = await instance.ssoSilent(silentRequest);
            if (silentResult.account) {
              instance.setActiveAccount(silentResult.account);
            }
          } catch (silentError) {
            if (silentError instanceof InteractionRequiredAuthError) {
              // Silent auth failed, redirect to login
              await instance.loginRedirect(loginRequest);
            } else {
              console.error('Silent authentication error:', silentError);
              setError('Authentication failed. Please try again.');
            }
          }
        } else {
          // Set the first account as active
          instance.setActiveAccount(accounts[0]);
        }
      } catch (authError) {
        console.error('Authentication initialization error:', authError);
        setError('Failed to initialize authentication.');
      } finally {
        setIsLoading(false);
      }
    };

    // Only initialize if not already in progress
    if (inProgress === 'none') {
      initializeAuth();
    } else {
      setIsLoading(false);
    }
  }, [instance, accounts, inProgress]);

  const getAccessToken = async (scopes: string[]): Promise<string> => {
    try {
      const activeAccount = instance.getActiveAccount();
      
      if (!activeAccount) {
        throw new Error('No active account found');
      }

      const request = {
        scopes,
        account: activeAccount,
        forceRefresh: false,
      };

      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Use popup for interactive authentication to avoid page redirect
        const activeAccount = instance.getActiveAccount();
        
        const request = {
          scopes,
          account: activeAccount || undefined, // Convert null to undefined for TypeScript
        };
        
        const response = await instance.acquireTokenPopup(request);
        return response.accessToken;
      }
      throw error;
    }
  };

  const login = async () => {
    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (loginError) {
      console.error('Login error:', loginError);
      setError('Login failed. Please try again.');
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await instance.logoutRedirect();
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
      setError('Logout failed. Please try again.');
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    account,
    login,
    logout,
    error,
    getAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};